import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Camera, GoogleLogo, CheckCircle, User, Warning, Sparkle, ArrowsClockwise, Lock } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PatientAutocomplete } from '@/components/PatientAutocomplete';

const API = process.env.REACT_APP_BACKEND_URL;

function ChangePasswordSection() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current: '', newPw: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');
    if (!form.current || !form.newPw || !form.confirm) { setError('Preenche todos os campos'); return; }
    if (form.newPw.length < 8) { setError('A nova palavra-passe deve ter pelo menos 8 caracteres'); return; }
    if (form.newPw !== form.confirm) { setError('As palavras-passe não coincidem'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/auth/change-password`, { current_password: form.current, new_password: form.newPw });
      toast.success('Palavra-passe alterada com sucesso');
      setForm({ current: '', newPw: '', confirm: '' }); setOpen(false);
    } catch (e) { setError(e.response?.data?.detail || 'Erro ao alterar'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-5 mt-5" data-testid="change-password-section">
      <h2 className="font-heading text-sm text-brand-text mb-1 flex items-center gap-2"><Lock size={18} className="text-brand-green" /> Segurança</h2>
      {!open ? (
        <button onClick={() => setOpen(true)} className="w-full text-left flex items-center gap-3 p-2 mt-2 rounded-xl hover:bg-brand-bg transition-colors" data-testid="change-password-btn">
          <div><p className="text-sm text-brand-text">Alterar palavra-passe</p><p className="text-[10px] text-brand-muted">Muda a tua palavra-passe de acesso</p></div>
        </button>
      ) : (
        <div className="space-y-3 mt-3">
          <div><Label className="text-xs text-brand-muted mb-1 block">Palavra-passe actual</Label><Input type="password" value={form.current} onChange={e => setForm({ ...form, current: e.target.value })} className="rounded-xl border-brand-border" data-testid="current-password" /></div>
          <div><Label className="text-xs text-brand-muted mb-1 block">Nova palavra-passe</Label><Input type="password" value={form.newPw} onChange={e => setForm({ ...form, newPw: e.target.value })} placeholder="Mínimo 8 caracteres" className="rounded-xl border-brand-border" data-testid="new-password" /></div>
          <div><Label className="text-xs text-brand-muted mb-1 block">Confirmar nova palavra-passe</Label><Input type="password" value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })} className="rounded-xl border-brand-border" data-testid="confirm-password" /></div>
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex gap-2">
            <button onClick={handleSubmit} disabled={saving} className="flex-1 bg-brand-green text-white rounded-full py-2.5 text-sm font-medium disabled:opacity-50" data-testid="submit-password-btn">{saving ? 'A alterar...' : 'Alterar palavra-passe'}</button>
            <button onClick={() => { setOpen(false); setError(''); setForm({ current: '', newPw: '', confirm: '' }); }} className="px-4 border border-brand-border text-brand-muted rounded-full py-2.5 text-sm">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MinhaContaPage() {
  const { user, refreshUser } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: '', email: '', license_number: '', signature_short: '', professional_title: '' });
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [gcalStatus, setGcalStatus] = useState(null);
  const [watchStatus, setWatchStatus] = useState(null);
  const [unresolvedNotes, setUnresolvedNotes] = useState([]);
  const [allPatients, setAllPatients] = useState([]);
  const [assignSelections, setAssignSelections] = useState({});
  const [meetCronStatus, setMeetCronStatus] = useState(null);
  const [runningMeetCron, setRunningMeetCron] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || '', email: user.email || '', license_number: user.license_number || '', signature_short: user.signature_short || '', professional_title: user.professional_title || '' });
  }, [user]);

  const fetchUnresolvedNotes = () => {
    if (!isAdmin) return;
    axios.get(`${API}/api/admin/meet-notes/unresolved`).then(({ data }) => setUnresolvedNotes(Array.isArray(data) ? data : [])).catch(() => {});
  };

  const fetchMeetCronStatus = () => {
    if (!isAdmin) return;
    axios.get(`${API}/api/admin/cron/status/meet_smart_notes`).then(({ data }) => setMeetCronStatus(data)).catch(() => {});
  };

  useEffect(() => {
    const g = searchParams.get('gcal');
    if (g === 'success') toast.success('Google Calendar ligado com sucesso');
    if (g === 'error') toast.error('Erro ao ligar o Google Calendar');
    (async () => {
      try { const { data } = await axios.get(`${API}/api/google-calendar/status`); setGcalStatus(data); } catch {}
      try { const { data } = await axios.get(`${API}/api/google-calendar/watch-status`); setWatchStatus(data); } catch {}
    })();
    fetchUnresolvedNotes();
    fetchMeetCronStatus();
    // "all=true": esta zona serve para associar resumos de QUALQUER nutricionista à
    // paciente certa, por isso mostra sempre todas as pacientes da clínica, independente
    // do toggle "Ver toda a equipa".
    if (isAdmin) axios.get(`${API}/api/patients?search=&all=true&limit=2000`).then(({ data }) => setAllPatients(Array.isArray(data) ? data : [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const associateNote = async (draftId) => {
    const patientId = assignSelections[draftId];
    if (!patientId) { toast.error('Escolhe uma paciente'); return; }
    try { await axios.post(`${API}/api/meet-notes/${draftId}/associate`, { patient_id: patientId }); toast.success('Associado, vai aparecer na ficha da paciente'); fetchUnresolvedNotes(); }
    catch { toast.error('Erro ao associar'); }
  };

  const runMeetCronNow = async () => {
    setRunningMeetCron(true);
    try {
      await axios.post(`${API}/api/admin/cron/run/meet_smart_notes`);
      toast.success('Verificação concluída');
      fetchUnresolvedNotes();
      fetchMeetCronStatus();
    } catch { toast.error('Erro ao verificar'); }
    finally { setRunningMeetCron(false); }
  };

  const saveProfile = async () => {
    if (!form.name.trim()) { toast.error('O nome é obrigatório'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/my-profile`, { name: form.name.trim(), email: form.email.trim(), license_number: form.license_number.trim(), signature_short: form.signature_short.trim(), professional_title: form.professional_title.trim() });
      toast.success('Dados atualizados');
      if (refreshUser) refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao guardar'); }
    finally { setSaving(false); }
  };

  const handleAvatarUpload = async (file) => {
    if (!file) return;
    setAvatarUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/api/my-profile/avatar`, fd);
      toast.success('Foto atualizada');
      if (refreshUser) refreshUser();
    } catch { toast.error('Erro ao carregar foto'); }
    finally { setAvatarUploading(false); }
  };

  const connectGcal = async () => {
    try { const { data } = await axios.get(`${API}/api/google-calendar/auth-url`); window.location.href = data.auth_url; }
    catch { toast.error('Erro ao iniciar ligação'); }
  };
  const disconnectGcal = async () => {
    try { await axios.post(`${API}/api/google-calendar/disconnect`); setGcalStatus({ connected: false }); toast.success('Google Calendar desligado'); }
    catch { toast.error('Erro'); }
  };
  const startWatch = async () => {
    try { await axios.post(`${API}/api/google-calendar/watch`); setWatchStatus({ active: true }); toast.success('Sincronização bidirecional ativada!'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro ao ativar'); }
  };
  const stopWatch = async () => {
    try { await axios.post(`${API}/api/google-calendar/stop-watch`); setWatchStatus({ active: false }); toast.success('Sincronização bidirecional desativada'); }
    catch { toast.error('Erro'); }
  };

  const avatarUrl = user?.avatar_path ? `${API}/api/files/${user.avatar_path}` : null;

  return (
    <div className="fade-in max-w-2xl" data-testid="minha-conta-page">
      <h1 className="font-heading font-medium text-xl text-brand-text mb-5">Definições</h1>

      {/* Dados pessoais */}
      <div className="bg-white border border-brand-border rounded-2xl p-5 mb-5" data-testid="personal-data-section">
        <h2 className="font-heading text-sm text-brand-text mb-4">Dados pessoais</h2>
        <div className="flex items-center gap-4 mb-4">
          <div className="relative">
            {avatarUrl ? (
              <img src={avatarUrl} alt="Avatar" className="w-16 h-16 rounded-full object-cover border-2 border-brand-green/20" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-brand-mint/20 flex items-center justify-center"><User size={28} className="text-brand-green" /></div>
            )}
            <label className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-brand-green text-white flex items-center justify-center cursor-pointer hover:bg-brand-green-hover shadow-sm" data-testid="upload-avatar-btn">
              <Camera size={14} />
              <input type="file" accept="image/*" onChange={e => handleAvatarUpload(e.target.files?.[0])} className="hidden" disabled={avatarUploading} />
            </label>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-brand-text truncate">{user?.name}</p>
            <p className="text-xs text-brand-muted capitalize">{user?.role}</p>
          </div>
        </div>
        <div className="space-y-3">
          <div><Label className="text-xs text-brand-muted mb-1 block">Nome</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-xl border-brand-border" data-testid="profile-name-input" /></div>
          <div><Label className="text-xs text-brand-muted mb-1 block">Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-xl border-brand-border" data-testid="profile-email-input" /></div>
          <div>
            <Label className="text-xs text-brand-muted mb-1 block">Cédula profissional</Label>
            <Input value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })} placeholder="Ex: 2758N" className="rounded-xl border-brand-border w-40" data-testid="license-number-input" />
            <p className="text-[10px] text-brand-placeholder mt-1">Usada nas declarações e documentos que gerares (assinatura).</p>
          </div>
          {['admin', 'nutricionista'].includes(user?.role) && (
            <>
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Assinatura curta</Label>
                <Input value={form.signature_short} onChange={e => setForm({ ...form, signature_short: e.target.value })} placeholder="Ex: Ana Magalhães Pereira | Equipa nutrianapereira" className="rounded-xl border-brand-border" data-testid="signature-short-input" />
                <p className="text-[10px] text-brand-placeholder mt-1">Usada no fim dos emails, notificações e push enviados às pacientes.</p>
              </div>
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Título profissional</Label>
                <Input value={form.professional_title} onChange={e => setForm({ ...form, professional_title: e.target.value })} placeholder="Ex: Nutricionista na área materno-infantil" className="rounded-xl border-brand-border" data-testid="professional-title-input" />
                <p className="text-[10px] text-brand-placeholder mt-1">Aparece numa segunda linha da assinatura dos emails.</p>
              </div>
            </>
          )}
          <button onClick={saveProfile} disabled={saving} className="bg-brand-green text-white rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-50" data-testid="save-profile-btn">{saving ? 'A guardar...' : 'Guardar alterações'}</button>
        </div>
      </div>

      {/* Alterar password */}
      <ChangePasswordSection />

      {/* Google Calendar */}
      <div className="bg-white border border-brand-border rounded-2xl p-5" data-testid="gcal-section">
        <h2 className="font-heading text-sm text-brand-text mb-1 flex items-center gap-2"><GoogleLogo size={18} className="text-brand-green" /> Google Calendar</h2>
        <p className="text-xs text-brand-muted mb-3">Liga a tua conta Google para sincronizar consultas e detetar horários ocupados no agendamento público.</p>
        {gcalStatus?.connected ? (
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="flex items-center gap-2 text-sm text-green-600"><CheckCircle size={18} weight="fill" /> Ligado{gcalStatus.connected_at && <span className="text-[10px] text-brand-muted">desde {gcalStatus.connected_at.split('T')[0]}</span>}</span>
            <button onClick={disconnectGcal} className="text-xs text-red-500 hover:underline" data-testid="gcal-disconnect">Desligar integração</button>
          </div>
        ) : (
          <button onClick={connectGcal} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium" data-testid="gcal-connect">Ligar Google Calendar</button>
        )}
        {gcalStatus?.connected && (
          <div className="mt-3 bg-brand-bg/30 rounded-xl p-3" data-testid="gcal-watch-section">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-brand-text">Sincronização bidirecional</span>
              {watchStatus?.active ? (
                <span className="text-[9px] bg-brand-green/10 text-brand-green px-2 py-0.5 rounded-full">Ativa</span>
              ) : (
                <span className="text-[9px] bg-brand-bg text-brand-muted px-2 py-0.5 rounded-full">Inativa</span>
              )}
            </div>
            <p className="text-[10px] text-brand-muted mb-2">Quando ativa, alterações feitas directamente no Google Calendar (editar hora, cancelar) são refletidas automaticamente na app.</p>
            {watchStatus?.active ? (
              <button onClick={stopWatch} className="text-xs text-amber-600 hover:underline" data-testid="gcal-stop-watch">Desativar sync bidirecional</button>
            ) : (
              <button onClick={startWatch} className="text-xs text-brand-green font-medium hover:underline" data-testid="gcal-start-watch">Ativar sync bidirecional</button>
            )}
          </div>
        )}
        {gcalStatus?.connected && !gcalStatus?.meet_notes_enabled && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2" data-testid="meet-notes-reconnect-banner">
            <Warning size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs text-amber-800">Para importar automaticamente os resumos do Gemini das reuniões Google Meet para a ficha das pacientes, é preciso reautorizar com 2 permissões extra (leitura do Meet e dos Docs).</p>
              <button onClick={connectGcal} className="mt-2 bg-amber-600 text-white rounded-full px-3 py-1.5 text-xs font-medium" data-testid="meet-notes-reconnect-btn">Reautorizar agora</button>
            </div>
          </div>
        )}
      </div>

      {/* Diagnóstico Gemini / Meet — data da última verificação do cron + trigger manual */}
      {isAdmin && (
        <div className="bg-white border border-brand-border rounded-2xl p-5 mt-5" data-testid="meet-cron-diagnostic-section">
          <h2 className="font-heading text-sm text-brand-text mb-1 flex items-center gap-2"><ArrowsClockwise size={18} className="text-brand-green" /> Diagnóstico Gemini / Meet</h2>
          <p className="text-xs text-brand-muted mb-3">Verificação horária automática de novos resumos do Gemini nas reuniões do Google Meet.</p>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-brand-muted" data-testid="meet-cron-last-run">
              {meetCronStatus?.last_run_at ? (
                <>Última verificação: <span className="text-brand-text font-medium">{meetCronStatus.last_run_at.replace('T', ' ').slice(0, 16)}</span>
                  {meetCronStatus.status === 'failed' && <span className="text-red-500 ml-1">(falhou{meetCronStatus.error ? `: ${meetCronStatus.error}` : ''})</span>}
                </>
              ) : 'Ainda sem registo de execução.'}
            </p>
            <button onClick={runMeetCronNow} disabled={runningMeetCron} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium disabled:opacity-50 flex items-center gap-1.5" data-testid="run-meet-cron-btn">
              <ArrowsClockwise size={13} className={runningMeetCron ? 'animate-spin' : ''} /> {runningMeetCron ? 'A verificar...' : 'Verificar agora'}
            </button>
          </div>
        </div>
      )}

      {/* Resumos do Gemini — não associados automaticamente (admin) */}
      {isAdmin && unresolvedNotes.length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-5 mt-5" data-testid="unresolved-meet-notes-section">
          <h2 className="font-heading text-sm text-brand-text mb-1 flex items-center gap-2"><Sparkle size={18} className="text-brand-gold-hover" /> Resumos do Gemini pendentes de associação</h2>
          <p className="text-xs text-brand-muted mb-3">Estas reuniões não corresponderam automaticamente a nenhuma marcação (ou correspondem a mais do que uma). Associa manualmente à paciente correta.</p>
          <div className="space-y-3">
            {unresolvedNotes.map(note => (
              <div key={note.id} className="border border-brand-border rounded-xl p-3" data-testid={`unresolved-note-${note.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-xs font-medium text-brand-text">{note.doc_title || 'Resumo sem título'}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${note.match_status === 'ambiguous' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-500'}`}>{note.match_status === 'ambiguous' ? 'Ambíguo' : 'Sem correspondência'}</span>
                </div>
                <p className="text-[10px] text-brand-muted mb-2">{(note.meeting_start || '').replace('T', ' ').slice(0, 16)}</p>
                <div className="flex gap-2">
                  <PatientAutocomplete
                    patients={allPatients}
                    value={assignSelections[note.id] || ''}
                    onChange={patientId => setAssignSelections({ ...assignSelections, [note.id]: patientId })}
                    testId={`assign-select-${note.id}`}
                  />
                  <button onClick={() => associateNote(note.id)} className="bg-brand-green text-white rounded-lg px-3 py-1.5 text-xs font-medium" data-testid={`assign-btn-${note.id}`}>Associar</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
